import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { decodeJWT, login as apiLogin } from '../lib/api'
import type { UserContext, JWTPayload, CommunitySettings } from '../lib/types'

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: UserContext | null
  token: string | null
  login: (email: string, password: string, communitySlug?: string) => Promise<void>
  logout: () => void
}

const AuthCtx = createContext<AuthState | null>(null)

function parseUserContext(payload: JWTPayload): UserContext {
  const isInternal = payload.communityType === 'gathering'
  let settings: CommunitySettings = {}
  if (payload.communitySettings) {
    settings = typeof payload.communitySettings === 'string'
      ? JSON.parse(payload.communitySettings)
      : payload.communitySettings
  }

  return {
    userType: isInternal ? 'internal' : 'community',
    userId: payload.userId,
    userName: payload.name,
    loginName: payload.loginName,
    communityId: payload.communityId || null,
    communitySlug: payload.communitySlug || null,
    communityName: isInternal ? 'The Gathering' : (payload.communityName || null),
    communityAccent: settings.accent_color || '#1B4965',
    communityFont: settings.font_display || null,
    isOperator: payload.role === 'operator',
    role: payload.role || 'member',
  }
}

function getToken(): string | null {
  // Prefer localStorage (survives browser cookie policies), fall back to cookie
  return localStorage.getItem('session_token') || (document.cookie.match(/session=([^;]+)/)?.[1] ?? null)
}

function persistToken(token: string) {
  localStorage.setItem('session_token', token)
  const secure = location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `session=${token}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax${secure}`
}

function clearToken() {
  localStorage.removeItem('session_token')
  document.cookie = 'session=; Max-Age=0; path=/'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<UserContext | null>(null)
  const [token, setToken] = useState<string | null>(null)

  // Restore session — try local token first, then ask server (handles Firefox insecure-origin cookie blocking)
  useEffect(() => {
    // Try the existing token BEFORE running any migration. The prior version of
    // this effect ran an unconditional Migration-v2 clearToken() whenever
    // localStorage 'auth_version' wasn't '2' — but Safari ITP and Firefox Total
    // Cookie Protection can wipe localStorage while leaving cookies intact, in
    // which case the migration killed valid sessions on every page load.
    // Now: validate the cookie/local token first; only run the migration if
    // no valid token is present AND there are v1-shaped artifacts to clean up.
    const existingToken = getToken()
    if (existingToken) {
      const payload = decodeJWT(existingToken) as JWTPayload | null
      if (payload && payload.exp * 1000 > Date.now()) {
        setToken(existingToken)
        setUser(parseUserContext(payload))
        setIsAuthenticated(true)
        setIsLoading(false)
        // Mark version current so we don't re-evaluate next load
        try { localStorage.setItem('auth_version', '2') } catch {}
        return
      } else {
        clearToken()
      }
    }

    // No valid token. Run Migration v2 cleanup if needed (clears any v1
    // artifacts from pre-Hetzner auth system).
    const authVersion = localStorage.getItem('auth_version')
    if (authVersion !== '2') {
      const hasV1Artifacts =
        localStorage.getItem('auth_token') ||
        localStorage.getItem('jwt') ||
        localStorage.getItem('user')
      if (hasV1Artifacts) {
        try { localStorage.removeItem('auth_token') } catch {}
        try { localStorage.removeItem('jwt') } catch {}
        try { localStorage.removeItem('user') } catch {}
      }
      try { localStorage.setItem('auth_version', '2') } catch {}
    }
    // No local token — check if server has a session cookie (Firefox may block JS cookie access)
    fetch('/api/auth/me', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.authenticated && data.token) {
          setToken(data.token)
          const payload = decodeJWT(data.token) as JWTPayload
          setUser(parseUserContext(payload))
          setIsAuthenticated(true)
          // Try to persist locally for next time
          try { localStorage.setItem('session_token', data.token) } catch {}
          try { document.cookie = `session=${data.token}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax` } catch {}
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [])

  // Theme application moved to ThemeContext — reads user from useAuth()

  const login = useCallback(async (email: string, password: string, communitySlug?: string) => {
    const res = await apiLogin(email, password, communitySlug)
    if (res.error) throw new Error(res.error)
    if (!res.token) throw new Error('No token returned')

    persistToken(res.token)
    setToken(res.token)

    const payload = decodeJWT(res.token) as JWTPayload
    setUser(parseUserContext(payload))
    setIsAuthenticated(true)
  }, [])

  const logout = useCallback(() => {
    clearToken()
    setToken(null)
    setUser(null)
    setIsAuthenticated(false)
  }, [])

  return (
    <AuthCtx.Provider value={{ isAuthenticated, isLoading, user, token, login, logout }}>
      {children}
    </AuthCtx.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthCtx)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
